/**
 * PAPPO — MainNav
 * Navegação principal adaptável, com dois formatos:
 *  - variant "rail":   trilha vertical fina à esquerda, só ícones (nome no hover) — PC.
 *  - variant "bottom": barra inferior estilo WhatsApp (ícone + nome) — celular ou PC opcional.
 * O mesmo conjunto de itens é usado nos dois formatos; a diferença é só visual (CSS).
 */
import { type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import {
  MessageCircle,
  Users,
  ShieldAlert,
  ShieldCheck,
  BookOpen,
  SlidersHorizontal,
  CircleUser,
  Gavel,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../contexts/NotificationsContext';
import styles from './MainNav.module.css';

type Item = { to: string; label: string; icon: ReactNode; badge: number };

export function MainNav({ variant }: { variant: 'rail' | 'bottom' }) {
  const { user } = useAuth();
  const { unreadMessages, pendingContacts, pendingConnections, unreadAlerts } =
    useNotifications();

  const size = variant === 'rail' ? 22 : 24;

  const items: Item[] = [
    { to: '/chat', label: 'Chat', icon: <MessageCircle size={size} />, badge: unreadMessages },
    { to: '/contacts', label: 'Contactos', icon: <Users size={size} />, badge: pendingContacts },
  ];

  if (user?.role === 'parent' || user?.role === 'admin') {
    items.push({
      to: '/parent-panel',
      label: 'Painel',
      icon: <ShieldAlert size={size} />,
      badge: pendingConnections + unreadAlerts,
    });
    items.push({
      to: '/instrucoes',
      label: 'Instruções',
      icon: <BookOpen size={size} />,
      badge: 0,
    });
    items.push({
      to: '/permissoes',
      label: 'Permissões',
      icon: <SlidersHorizontal size={size} />,
      badge: 0,
    });
  }

  // Perfil: logo abaixo de "Permissões" e visível a todos os utilizadores.
  items.push({
    to: '/perfil',
    label: 'Perfil',
    icon: <CircleUser size={size} />,
    badge: 0,
  });

  if (user?.role === 'admin') {
    items.push({ to: '/admin', label: 'Admin', icon: <ShieldCheck size={size} />, badge: 0 });
  }

  // Console de Staff (moderação) — visível só para a equipe (gerente/master/dev).
  if (user?.staff_role) {
    items.push({ to: '/staff', label: 'Staff', icon: <Gavel size={size} />, badge: 0 });
  }

  // Adulto ainda não verificado: leva à tela de verificação (email/telefone).
  const precisaVerificar =
    !!user && user.role !== 'child' && !(user.email_verified && user.phone_verified);
  if (precisaVerificar) {
    items.push({
      to: '/verificacao',
      label: 'Verificar',
      icon: <ShieldAlert size={size} />,
      badge: 0,
    });
  }

  return (
    <nav
      className={variant === 'rail' ? styles.rail : styles.bottom}
      role="navigation"
      aria-label="Navegação principal"
    >
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
        >
          <span className={styles.iconWrap}>
            {item.icon}
            {item.badge > 0 && (
              <span className={styles.badge}>{item.badge > 99 ? '99+' : item.badge}</span>
            )}
          </span>
          <span className={styles.label}>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
